import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Wand2, RotateCcw, Plus, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { WsSection, TagGroup, CountBadge } from "./shared";
import { t } from "@/lib/negotiationLabels";
import { translateFr as tfr } from "@/i18n";

export function SportProfileStep({ draft, update, playerId, config }) {
  const p = draft.profile || {};
  const [custom, setCustom] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const tags = config?.tags || { strengths: [], styles: [], environments: [] };

  const toggle = (cat, max) => (id) => {
    const cur = p[cat] || [];
    update(["profile", cat], cur.includes(id) ? cur.filter((x) => x !== id) : cur.length < max ? [...cur, id] : cur);
  };

  const setSummary = (text, keepPrev = true) => {
    if (keepPrev && p.summary) update(["profile", "summary_prev"], p.summary);
    update(["profile", "summary"], text);
    update(["profile", "summary_approved"], false);
  };

  const propose = async () => {
    try {
      const r = await api.post(`/players/${playerId}/negotiation-profile/draft/summary`, {
        profile: { strengths: p.strengths, styles: p.styles, environments: p.environments },
      });
      setSummary(r.data.summary);
    } catch { toast.error(tfr("Impossible de proposer un résumé")); }
  };

  const reformulate = async () => {
    if (!p.summary) return;
    setAiLoading(true);
    try {
      const r = await api.post(`/players/${playerId}/negotiation-profile/draft/summary/ai`, { summary: p.summary });
      setSummary(r.data.summary);
      toast.success(tfr("Résumé reformulé — relisez et approuvez avant inclusion"));
    } catch {
      toast.error(tfr("Reformulation IA indisponible — le résumé actuel est conservé"));
    } finally { setAiLoading(false); }
  };

  const strengthCount = (p.strengths || []).length + (p.custom_strengths || []).length;

  return (
    <div data-testid="ws-step-profile">
      <WsSection title={t("negotiationProfile.sportProfile.strengths")}
        subtitle={t("negotiationProfile.sportProfile.strengthsMax")}
        right={<CountBadge count={strengthCount} max={5} />} testid="profile-strengths">
        <TagGroup options={tags.strengths} selected={p.strengths || []}
          max={5 - (p.custom_strengths || []).length} onToggle={toggle("strengths", 5 - (p.custom_strengths || []).length)}
          testidPrefix="strength-tag" />
        {(p.custom_strengths || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {p.custom_strengths.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-athlynx text-white px-3 h-9 text-xs font-medium">
                <Check className="h-3 w-3" /> {c}
                <button onClick={() => update(["profile", "custom_strengths"], p.custom_strengths.filter((x) => x !== c))}
                  aria-label={`Retirer ${c}`} className="hover:opacity-70"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-3 max-w-sm">
          <Input value={custom} onChange={(e) => setCustom(e.target.value)}
            placeholder={t("negotiationProfile.sportProfile.customStrength")} className="h-9"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); } }}
            data-testid="custom-strength-input" />
          <Button variant="outline" size="sm" className="h-9 gap-1 shrink-0"
            disabled={!custom.trim() || strengthCount >= 5}
            onClick={() => { update(["profile", "custom_strengths"], [...(p.custom_strengths || []), custom.trim()]); setCustom(""); }}
            data-testid="custom-strength-add">
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </Button>
        </div>
      </WsSection>

      <WsSection title={t("negotiationProfile.sportProfile.playingStyle")}
        subtitle={t("negotiationProfile.sportProfile.styleMax")}
        right={<CountBadge count={(p.styles || []).length} max={3} />} testid="profile-styles">
        <TagGroup options={tags.styles} selected={p.styles || []} max={3} onToggle={toggle("styles", 3)} testidPrefix="style-tag" />
      </WsSection>

      <WsSection title={t("negotiationProfile.sportProfile.environment")}
        subtitle={t("negotiationProfile.sportProfile.envMax")}
        right={<CountBadge count={(p.environments || []).length} max={3} />} testid="profile-envs">
        <TagGroup options={tags.environments} selected={p.environments || []} max={3} onToggle={toggle("environments", 3)} testidPrefix="env-tag" />
      </WsSection>

      <WsSection title={t("negotiationProfile.sportProfile.summary")}
        subtitle={t("negotiationProfile.sportProfile.summaryHint")} testid="profile-summary">
        <div className="flex flex-wrap gap-2 mb-3">
          <Button variant="outline" size="sm" onClick={propose} className="h-8 gap-1.5 text-xs" data-testid="summary-propose-btn">
            <Sparkles className="h-3.5 w-3.5" /> {t("negotiationProfile.sportProfile.propose")}
          </Button>
          <Button variant="outline" size="sm" onClick={reformulate} disabled={!p.summary || aiLoading}
            className="h-8 gap-1.5 text-xs" data-testid="summary-ai-btn">
            <Wand2 className="h-3.5 w-3.5" /> {aiLoading ? "Reformulation…" : t("negotiationProfile.sportProfile.aiReformulate")}
          </Button>
          {p.summary_prev && (
            <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs"
              onClick={() => { const prev = p.summary_prev; update(["profile", "summary_prev"], p.summary || ""); update(["profile", "summary"], prev); update(["profile", "summary_approved"], false); }}
              data-testid="summary-restore-btn">
              <RotateCcw className="h-3.5 w-3.5" /> {t("negotiationProfile.sportProfile.restorePrev")}
            </Button>
          )}
        </div>
        {p.summary && !p.summary_approved && (
          <Badge variant="outline" className="text-[10px] mb-2 bg-info/10 text-info border-info/30">
            {t("negotiationProfile.sportProfile.summaryProposed")}
          </Badge>
        )}
        <Textarea value={p.summary || ""} rows={4} maxLength={900}
          onChange={(e) => { update(["profile", "summary"], e.target.value); update(["profile", "summary_approved"], false); }}
          placeholder={tfr("Le résumé apparaîtra ici — proposez-en un ou rédigez librement.")}
          aria-label={t("negotiationProfile.sportProfile.summary")}
          data-testid="summary-textarea" />
        <div className="mt-2">
          {p.summary_approved ? (
            <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30" data-testid="summary-approved-badge">
              <Check className="h-3 w-3 mr-1" /> {t("negotiationProfile.sportProfile.approved")}
            </Badge>
          ) : (
            <Button size="sm" onClick={() => update(["profile", "summary_approved"], true)} disabled={!p.summary}
              className="h-8 gap-1.5 text-xs bg-athlynx hover:bg-athlynx/90 text-white" data-testid="summary-approve-btn">
              <Check className="h-3.5 w-3.5" /> {t("negotiationProfile.sportProfile.approve")}
            </Button>
          )}
        </div>
      </WsSection>
    </div>
  );
}
